import React from "react";

/**
 * Ulties brand logo — inline SVG so we can swap fills via currentColor
 * (which means it adapts to dark/light theme + any tailwind text-* class).
 *
 * Variants:
 *   - "full"     U monogram + "lties" wordmark + "tech solutions" tagline
 *   - "wordmark" U monogram + "lties" wordmark (default, used in nav)
 *   - "mark"     U monogram only (favicon / mobile / compact)
 */
export default function Logo({
  variant = "wordmark",
  className = "",
  style,
  title = "Ulties — tech solutions",
  ...rest
}) {
  const showU = true;
  const showWordmark = variant !== "mark";
  const showTagline = variant === "full";

  // Native viewBox is 326.76 × 164.16.
  // The U glyph spans roughly y=4 → y=160, so any wordmark viewBox must keep
  // height ≥ 162. We only shrink horizontally for the mark-only variant.
  const viewBox = showTagline
    ? "0 0 326.76 164.16"
    : showWordmark
      ? "0 0 326.76 162"
      : "0 0 116 162";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      role="img"
      aria-label={title}
      className={className}
      style={{ color: "currentColor", ...style }}
      {...rest}
    >
      <title>{title}</title>
      {showTagline && (
        <text
          x="110.04"
          y="151.4"
          fill="currentColor"
          style={{
            fontFamily: '"Cabinet Grotesk", "Helvetica Neue", system-ui, sans-serif',
            fontSize: 26,
            letterSpacing: "0.15em",
            fontWeight: 500,
          }}
        >
          tech solutions
        </text>
      )}

      {showWordmark && (
        <>
          {/* s */}
          <path
            fill="currentColor"
            d="M303.14,124.8c-8.96,0-16.14-1.53-21.52-4.59l1.49-10.9c2.43,1.33,5.53,2.52,9.3,3.57,3.76,1.05,7.19,1.58,10.29,1.58s5.71-.6,7.5-1.8c1.79-1.2,2.7-2.96,2.74-5.28,0-2.1-.84-3.76-2.52-4.98-1.68-1.22-4.88-2.73-9.6-4.54-1.62-.59-2.67-.98-3.15-1.16-5.61-2.18-9.65-4.6-12.12-7.27-2.47-2.67-3.71-6.3-3.71-10.87,0-5.53,2-9.77,6-12.72,4-2.95,9.58-4.43,16.74-4.43,7.75,0,14.55,1.46,20.42,4.37l-3.6,10.01c-5.79-2.69-11.31-4.04-16.54-4.04-2.91,0-5.19.49-6.83,1.47-1.64.98-2.46,2.46-2.46,4.45,0,1.88.79,3.35,2.38,4.4,1.58,1.05,4.68,2.42,9.3,4.12.15.04.63.21,1.44.5.81.29,1.44.53,1.88.72,5.57,2.03,9.67,4.45,12.29,7.28,2.62,2.82,3.93,6.5,3.93,11.04-.04,6.09-2.11,10.79-6.2,14.11-4.09,3.32-9.9,4.98-17.43,4.98Z"
          />
          {/* ie */}
          <g>
            <path
              fill="currentColor"
              d="M199.15,54.03h14.55v-12.39h-14.55v12.39ZM269.51,69.25c-4.63-5.24-11.13-7.86-19.5-7.86-8.85.04-15.84,2.92-20.97,8.63-2.44,2.72-4.27,5.89-5.55,9.5,8.09-2.32,15.63-4.45,20.5-6.38,1.82-.93,3.84-1.41,6.08-1.41,3.98,0,7.1,1.33,9.35,3.98,2.25,2.65,3.38,6.45,3.38,11.4h-26.56s0-.07.01-.11h-14.5c-.25,1.94-.4,3.96-.4,6.09,0,9.92,2.72,17.7,8.16,23.33,5.44,5.63,13.05,8.44,22.83,8.44,8.41,0,15.66-1.88,21.74-5.64l-2.05-10.29c-6.12,3.47-12.51,5.2-19.14,5.2-5.2,0-9.29-1.56-12.26-4.68-2.97-3.12-4.53-7.52-4.67-13.2h40.45c0-.66,0-1.69.03-3.1.02-1.4.03-2.41.03-3.04,0-8.67-2.31-15.62-6.95-20.86ZM199.15,123.7h14.5v-36.69h-13.91c4.21-1.6,9.01-3.13,13.91-4.61v-19.51h-14.5v60.81Z"
            />
          </g>
          {/* t */}
          <path
            fill="currentColor"
            d="M183.2,124.53c-4.13,0-7.57-.49-10.32-1.47-2.75-.98-4.9-2.52-6.44-4.62-1.55-2.1-2.65-4.58-3.29-7.44-.65-2.86-.97-6.39-.97-10.6v-28.72h-9.96l.55-7.86,10.4-.94,4.65-16.49,8.85-.06v16.55h15.11v8.8h-15.11v28.99c0,4.61.66,7.89,1.99,9.85,1.33,1.96,3.78,2.93,7.36,2.93,2.1,0,4.35-.18,6.75-.55l.39,10.4c-.08,0-.53.08-1.36.25-.83.16-1.62.31-2.38.44-.76.13-1.72.25-2.91.36-1.18.11-2.29.16-3.32.16Z"
          />
          {/* l */}
          <path
            fill="currentColor"
            d="M146.01,124.53c-4.17,0-7.63-.49-10.38-1.47-2.75-.98-4.92-2.54-6.5-4.68-1.59-2.14-2.69-4.68-3.32-7.63-.63-2.95-.94-6.62-.94-11.01v-59.26l14.5-.94v59.92c0,5.46.64,9.16,1.93,11.09,1.29,1.94,3.8,2.91,7.53,2.91,1.84,0,4.18-.2,7.03-.61l.39,10.34s-.49.09-1.36.28c-.87.18-1.7.34-2.49.47-.8.13-1.8.26-3.02.39-1.22.13-2.34.19-3.37.19Z"
          />
        </>
      )}

      {showU && (
        <g>
          <path
            fill="currentColor"
            d="M28.78,48.82v65.02c0,21.93,11.38,37.5,28.31,38.75l-1.51-4.54c-4.38,3.82-8.97,6.7-13.65,8.57-8.91,3.55-17.45,3.05-24.05-1.42-8.07-5.47-12.7-16.36-12.7-29.88V47.09l-1.63,2.41,18.37-7.32s2.96-1.08,4.79.18c1.72,1.18,2.08,4.17,2.08,6.47ZM20,37.36L1.63,44.68l-1.63.65v79.98c0,15.27,5.46,27.72,14.97,34.17,8.06,5.47,18.32,6.16,28.88,1.95,5.22-2.08,10.31-5.27,15.14-9.47l4.68-4.08-6.2-.46c-14.28-1.05-23.5-14.24-23.5-33.58V48.82c0-5.18-1.47-8.81-4.37-10.77-4.08-2.77-9.05-.91-9.6-.69Z"
          />
          <path
            fill="currentColor"
            d="M80.59,29.19v64c0,30.64-10.84,42.52-25.01,54.87l-4.69,4.08,6.2.46c5,.37,10.23-.51,15.54-2.63,26.65-10.63,41.93-35.5,41.93-68.25V0l-3.55,1.42-19.46,7.76.32-.1c-.46.12-11.28,3.06-11.28,20.11ZM93.47,13.99l19.46-7.76-3.55-2.41v77.88c0,30.52-14.09,53.64-38.66,63.43-4.57,1.82-9.03,2.59-13.24,2.28l1.51,4.54c14.66-12.77,26.79-26.01,26.79-58.78V29.19c0-12.83,7.1-15.01,7.4-15.1l.13-.04s.16-.06.16-.06Z"
          />
        </g>
      )}
    </svg>
  );
}
